import * as React from 'react';
import { QuotesCollectionFieldsFragment } from 'types/generated';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document } from '@contentful/rich-text-types';
import { Paragraph, Subheading } from 'components/src/typography';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { ImageWithFocalPoint } from '../ImageWithFocalPoint';
import { getOr } from '@/utils/options';
import clsx from 'clsx';
import { useRef, useState } from 'react';

type Props = {
  data: QuotesCollectionFieldsFragment;
};

const QuotesCollection = ({ data }: Props) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const swiperRef = useRef(null);
  const collection = data.quotesCollection;
  return (
    <div className="relative grid grid-cols-12 md:gap-x-24">
      <div
        className={`col-span-12 col-start-1 grid grid-cols-6 self-start px-16 py-16 md:col-span-6 md:col-start-1 ${clsx(
          {
            'rounded-28 bg-white-100 !px-32 py-32 md:!col-span-12': data.sideImage === null,
          }
        )}`}
      >
        <Swiper
          ref={swiperRef}
          onSlideChange={(swiper) => setActiveSlideIndex(swiper.realIndex)}
          modules={[Pagination, A11y, Autoplay]}
          pagination={{
            clickable: true,
          }}
          mousewheel={{
            forceToAxis: true,
          }}
          effect={'cards'}
          autoplay={{
            delay: getOr(data.delay, 10) * 1000,
          }}
          spaceBetween={0}
          slidesPerView={1}
          className={`!col-span-full !-mx-32 !flex [&_.swiper-pagination-bullet]:!w-[12px] [&_.swiper-pagination-bullets]:mx-16 [&_.swiper-pagination-bullets]:text-left [&_.swiper-pagination]:row-start-3 ${clsx(
            {
              '[&_.swiper-pagination-bullets]:lg:mx-128': data.sideImage === null,
            }
          )}
          `}
        >
          {collection?.items &&
            collection.items.map(function (quote, i) {
              return (
                quote &&
                quote.name && (
                  <SwiperSlide key={i}>
                    <div
                      className={`col-span-full mx-16 grid grid-cols-[80px_1fr] grid-rows-[80px_1fr] gap-x-16 ${clsx({
                        'lg:mx-128': data.sideImage === null,
                      })}${clsx({
                        '!grid-cols-1 !grid-rows-1 ': quote.image === null,
                      })}
                      `}
                    >
                      {quote.image && (
                        <div className="relative col-start-1 row-start-1 h-80 w-80">
                          <ImageWithFocalPoint
                            image={quote.image}
                            sectionId={quote.image.id}
                            className="self-center rounded-full"
                            sizes={{ default: '80px' }}
                          />
                        </div>
                      )}
                      <div
                        className={`col-start-2 row-start-1 self-center ${clsx({
                          '!col-start-1 ': quote.image === null,
                        })}
                      `}
                      >
                        <Subheading bold={true} classes="text-black-100">
                          {quote.name}
                        </Subheading>
                        {quote.title && (
                          <Paragraph variant="medium" classes="text-black-100">
                            {quote.title}
                          </Paragraph>
                        )}
                      </div>
                      <div className={`col-span-2 col-start-1 pt-16`}>
                        {quote.quote &&
                          documentToReactComponents(quote.quote.json as Document, {
                            renderNode: {
                              [BLOCKS.PARAGRAPH]: (_node, children) => {
                                return (
                                  <Paragraph variant="medium" classes="text-black-100">
                                    {children}
                                  </Paragraph>
                                );
                              },
                            },
                          })}
                      </div>
                    </div>
                  </SwiperSlide>
                )
              );
            })}
        </Swiper>
      </div>
      <div className="absolute bottom-0 left-0 z-50 flex">
        {collection?.items.map((_, index) => (
          <div
            key={index}
            onClick={() =>
              (swiperRef.current as unknown as { swiper: { slideTo(index: number): void } })?.swiper.slideTo(index)
            }
            className="cursor-pointer"
            style={{ padding: '5px' }}
          >
            <div
              style={{
                minHeight: '10px',
                minWidth: '10px',
                backgroundColor: index === activeSlideIndex ? '#F14157' : '#D2D2D3',
                borderRadius: '50%',
              }}
            ></div>
          </div>
        ))}
      </div>
      {data.sideImage && (
        <div className="relative col-span-12 my-32 grid h-[330px] items-center justify-items-center self-start md:col-span-6">
          <ImageWithFocalPoint image={data.sideImage} sectionId={data.sideImage.id} className="rounded-28" />
        </div>
      )}
    </div>
  );
};
export default QuotesCollection;
